<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import cortexProviderConnectionsAPI from 'dashboard/api/cortexProviderConnections';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Label from 'dashboard/components-next/label/Label.vue';

const emit = defineEmits(['startSetup', 'editSetup', 'configureRoutes']);

const { t, locale } = useI18n();
const connections = ref([]);
const isLoading = ref(false);
const deletionDialog = ref(null);
const platformConnectionsDialog = ref(null);
const pendingDeletion = ref(null);
const testingConnectionIds = ref(new Set());
const adoptingConnectionId = ref(null);
const platformConnections = ref([]);

const hasActiveCredential = computed(() =>
  connections.value.some(
    connection =>
      connection.status === 'active' &&
      connection.credentials.some(credential => credential.status === 'active')
  )
);

const ownerLabel = connection =>
  connection.read_only
    ? t('CORTEX_MODELS.CONNECTIONS.PLATFORM')
    : t('CORTEX_MODELS.CONNECTIONS.ACCOUNT');

const hasConfiguredCredential = connection =>
  connection.credentials.some(credential => credential.status === 'active');

const connectionStatusColor = connection =>
  connection.status === 'active' ? 'teal' : 'slate';

const deletionSuccessLabel = connection =>
  connection.read_only
    ? t('CORTEX_MODELS.CONNECTIONS.PLATFORM_REMOVED')
    : t('CORTEX_MODELS.CONNECTIONS.DELETED');

const deletionActionLabel = connection =>
  connection.read_only
    ? t('CORTEX_MODELS.ACTIONS.REMOVE_PLATFORM_CONNECTION')
    : t('CORTEX_MODELS.ACTIONS.DELETE_CONNECTION');

const platformConnectionActionLabel = connection =>
  connection.adopted
    ? t('CORTEX_MODELS.CONNECTIONS.PLATFORM_ADOPTED_LABEL')
    : t('CORTEX_MODELS.ACTIONS.USE_PLATFORM_CONNECTION');

const loadConnections = async () => {
  isLoading.value = true;
  try {
    const { data } = await cortexProviderConnectionsAPI.get();
    connections.value = data;
  } catch (error) {
    useAlert(t('CORTEX_MODELS.ERRORS.LOAD'));
  } finally {
    isLoading.value = false;
  }
};

const requestDeletion = connection => {
  pendingDeletion.value = connection;
  deletionDialog.value?.open();
};

const openPlatformConnections = async () => {
  try {
    const { data } =
      await cortexProviderConnectionsAPI.listPlatformConnections();
    platformConnections.value = data;
    platformConnectionsDialog.value?.open();
  } catch (error) {
    useAlert(t('CORTEX_MODELS.ERRORS.LOAD'));
  }
};

const adoptPlatformConnection = async connection => {
  adoptingConnectionId.value = connection.id;
  try {
    const { data } = await cortexProviderConnectionsAPI.adoptPlatformConnection(
      connection.id
    );
    connections.value = [...connections.value, data];
    platformConnections.value = platformConnections.value.map(item =>
      item.id === connection.id ? data : item
    );
    useAlert(t('CORTEX_MODELS.CONNECTIONS.PLATFORM_ADOPTED'));
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] || t('CORTEX_MODELS.ERRORS.SAVE')
    );
  } finally {
    adoptingConnectionId.value = null;
  }
};

const confirmDeletion = async () => {
  const connection = pendingDeletion.value;
  if (!connection) return;

  try {
    if (connection.read_only) {
      await cortexProviderConnectionsAPI.removePlatformConnection(
        connection.id
      );
    } else {
      await cortexProviderConnectionsAPI.delete(connection.id);
    }
    connections.value = connections.value.filter(
      item => item.id !== connection.id
    );
    useAlert(deletionSuccessLabel(connection));
    pendingDeletion.value = null;
    deletionDialog.value?.close();
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] || t('CORTEX_MODELS.ERRORS.DELETE')
    );
  }
};

const healthLabel = connection => {
  const labels = {
    online: t('CORTEX_MODELS.CONNECTIONS.HEALTH.ONLINE'),
    authentication_failed: t(
      'CORTEX_MODELS.CONNECTIONS.HEALTH.AUTHENTICATION_FAILED'
    ),
    under_pressure: t('CORTEX_MODELS.CONNECTIONS.HEALTH.UNDER_PRESSURE'),
    offline: t('CORTEX_MODELS.CONNECTIONS.HEALTH.OFFLINE'),
    incompatible: t('CORTEX_MODELS.CONNECTIONS.HEALTH.INCOMPATIBLE'),
    unconfigured: t('CORTEX_MODELS.CONNECTIONS.HEALTH.UNCONFIGURED'),
  };
  return connection.health?.status
    ? labels[connection.health.status] ||
        t('CORTEX_MODELS.CONNECTIONS.HEALTH.UNKNOWN')
    : t('CORTEX_MODELS.CONNECTIONS.HEALTH.NOT_TESTED');
};
const healthBadgeLabel = connection => {
  if (connection.health?.status === 'online') {
    return t('CORTEX_MODELS.CONNECTIONS.HEALTH.OPERATIONAL');
  }

  return connection.health?.status
    ? t('CORTEX_MODELS.CONNECTIONS.HEALTH.NOT_OPERATIONAL')
    : t('CORTEX_MODELS.CONNECTIONS.HEALTH.NOT_TESTED');
};
const healthBadgeColor = connection => {
  if (connection.health?.status === 'online') return 'teal';

  return connection.health?.status ? 'ruby' : 'slate';
};

const testConnection = async (connection, { notify = true } = {}) => {
  if (testingConnectionIds.value.has(connection.id)) return;

  testingConnectionIds.value.add(connection.id);
  try {
    const { data } = await cortexProviderConnectionsAPI.test(connection.id);
    const index = connections.value.findIndex(
      item => item.id === connection.id
    );
    if (index >= 0) connections.value[index] = data.connection;
    if (notify) {
      useAlert(
        ['online', 'under_pressure'].includes(data.health.status)
          ? t('CORTEX_MODELS.CONNECTIONS.TEST_SUCCESS')
          : t('CORTEX_MODELS.CONNECTIONS.TEST_FAILED')
      );
    }
  } catch (error) {
    if (notify) {
      useAlert(
        error.response?.data?.errors?.[0] ||
          t('CORTEX_MODELS.CONNECTIONS.TEST_FAILED')
      );
    }
  } finally {
    testingConnectionIds.value.delete(connection.id);
  }
};

const formatCheckedAt = value =>
  new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(new Date(value));

onMounted(async () => {
  await loadConnections();
  await Promise.allSettled(
    connections.value
      .filter(
        connection =>
          connection.status === 'active' && hasConfiguredCredential(connection)
      )
      .map(connection => testConnection(connection, { notify: false }))
  );
});
</script>

<template>
  <div class="flex flex-col gap-5" :class="{ 'opacity-60': isLoading }">
    <div
      class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
    >
      <div>
        <h4 class="text-heading-3 text-n-slate-12">
          {{ t('CORTEX_MODELS.CONNECTIONS.TITLE') }}
        </h4>
        <p class="mt-1 text-body-small text-n-slate-11">
          {{ t('CORTEX_MODELS.CONNECTIONS.SUMMARY_DESCRIPTION') }}
        </p>
      </div>
      <div class="flex flex-wrap items-center gap-2 shrink-0">
        <NextButton
          v-if="hasActiveCredential"
          :label="t('CORTEX_MODELS.ACTIONS.VIEW_ROUTES')"
          icon="i-lucide-route"
          slate
          outline
          @click="emit('configureRoutes')"
        />
        <NextButton
          data-testid="add-platform-connection"
          :label="t('CORTEX_MODELS.ACTIONS.ADD_PLATFORM_CONNECTION')"
          icon="i-lucide-building-2"
          slate
          outline
          @click="openPlatformConnections"
        />
        <NextButton
          data-testid="create-provider-configuration"
          :label="t('CORTEX_MODELS.ACTIONS.CREATE_OWN_CONNECTION')"
          icon="i-lucide-plus"
          @click="emit('startSetup')"
        />
      </div>
    </div>

    <div class="overflow-hidden border rounded-lg border-n-weak">
      <div
        v-if="!connections.length && !isLoading"
        class="flex flex-col items-center gap-3 px-4 py-8 text-center"
      >
        <span class="i-lucide-key-round size-6 text-n-slate-10" />
        <div>
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('CORTEX_MODELS.CONNECTIONS.EMPTY') }}
          </p>
          <p class="mt-1 text-body-small text-n-slate-11">
            {{ t('CORTEX_MODELS.CONNECTIONS.EMPTY_DESCRIPTION') }}
          </p>
        </div>
      </div>

      <div
        v-for="connection in connections"
        :key="connection.id"
        class="flex flex-col gap-3 px-4 py-3 border-b last:border-b-0 border-n-weak sm:flex-row sm:items-center"
      >
        <span
          class="flex items-center justify-center rounded-lg size-9 bg-n-alpha-2 shrink-0"
        >
          <span class="i-lucide-plug-zap size-4 text-n-slate-11" />
        </span>

        <div class="flex-1 min-w-0">
          <div class="flex flex-wrap items-center gap-2">
            <p class="text-sm font-medium truncate text-n-slate-12">
              {{ connection.name }}
            </p>
            <span class="text-xs text-n-slate-10">
              {{ ownerLabel(connection) }}
            </span>
            <Label
              :label="
                connection.status === 'active'
                  ? t('CORTEX_MODELS.CONNECTIONS.STATUS_ACTIVE')
                  : t('CORTEX_MODELS.CONNECTIONS.STATUS_INACTIVE')
              "
              :color="connectionStatusColor(connection)"
              compact
            >
              <template #icon>
                <span class="i-lucide-circle size-2.5 fill-current" />
              </template>
            </Label>
          </div>
          <p class="mt-1 text-xs truncate text-n-slate-10">
            {{ connection.provider_name }}
            {{ t('CORTEX_MODELS.SEPARATOR') }}
            {{ connection.quota_scope }}
            {{ t('CORTEX_MODELS.SEPARATOR') }}
            {{
              hasConfiguredCredential(connection)
                ? t('CORTEX_MODELS.CONNECTIONS.API_KEY_CONFIGURED')
                : t('CORTEX_MODELS.CONNECTIONS.API_KEY_MISSING')
            }}
          </p>
          <div
            class="mt-1 flex flex-wrap items-center gap-2 text-xs text-n-slate-10"
          >
            <Label
              :label="healthBadgeLabel(connection)"
              :color="healthBadgeColor(connection)"
              compact
            />
            <span
              v-if="
                connection.health?.status &&
                connection.health.status !== 'online'
              "
            >
              {{ healthLabel(connection) }}
            </span>
            <template
              v-if="
                connection.health?.latency_ms !== null &&
                connection.health?.latency_ms !== undefined
              "
            >
              {{ t('CORTEX_MODELS.SEPARATOR') }}
              {{
                t('CORTEX_MODELS.CONNECTIONS.HEALTH.LATENCY', {
                  latency: connection.health.latency_ms,
                })
              }}
            </template>
          </div>
          <p
            v-if="connection.health?.checked_at"
            class="mt-1 text-xs text-n-slate-10"
          >
            <time
              :datetime="connection.health.checked_at"
              data-testid="provider-connection-checked-at"
            >
              {{
                t('CORTEX_MODELS.CONNECTIONS.HEALTH.LAST_CHECKED', {
                  date: formatCheckedAt(connection.health.checked_at),
                })
              }}
            </time>
          </p>
        </div>

        <div class="flex items-center gap-1">
          <NextButton
            data-testid="test-provider-connection"
            :label="t('CORTEX_MODELS.ACTIONS.TEST_CONNECTION')"
            icon="i-lucide-plug-zap"
            slate
            ghost
            sm
            :is-loading="testingConnectionIds.has(connection.id)"
            :disabled="
              !hasConfiguredCredential(connection) ||
              testingConnectionIds.has(connection.id)
            "
            @click="testConnection(connection)"
          />
          <NextButton
            v-if="!connection.read_only"
            data-testid="edit-provider-connection"
            :label="t('CORTEX_MODELS.ACTIONS.EDIT_SETUP')"
            icon="i-lucide-pencil"
            slate
            ghost
            sm
            @click="emit('editSetup', connection)"
          />
          <NextButton
            icon="i-lucide-trash-2"
            ruby
            ghost
            sm
            :aria-label="deletionActionLabel(connection)"
            @click="requestDeletion(connection)"
          />
        </div>
      </div>
    </div>

    <Dialog
      ref="deletionDialog"
      type="alert"
      :title="t('CORTEX_MODELS.ACTIONS.DELETE_CONNECTION')"
      :description="t('CORTEX_MODELS.CONNECTIONS.DELETE_CONFIRM')"
      :confirm-button-label="t('CORTEX_MODELS.ACTIONS.DELETE_CONNECTION')"
      :cancel-button-label="t('CORTEX_MODELS.ACTIONS.CANCEL')"
      @confirm="confirmDeletion"
      @close="pendingDeletion = null"
    />

    <Dialog
      ref="platformConnectionsDialog"
      type="edit"
      width="lg"
      :title="t('CORTEX_MODELS.CONNECTIONS.PLATFORM_PICKER_TITLE')"
      :description="t('CORTEX_MODELS.CONNECTIONS.PLATFORM_PICKER_DESCRIPTION')"
      :show-confirm-button="false"
      :cancel-button-label="t('CORTEX_MODELS.ACTIONS.CANCEL')"
    >
      <div class="flex flex-col gap-2">
        <p v-if="!platformConnections.length" class="text-sm text-n-slate-11">
          {{ t('CORTEX_MODELS.CONNECTIONS.PLATFORM_EMPTY') }}
        </p>
        <div
          v-for="connection in platformConnections"
          :key="connection.id"
          class="flex items-center gap-3 p-3 border rounded-lg border-n-weak"
        >
          <span class="i-lucide-plug-zap size-4 text-n-slate-11" />
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-n-slate-12">
              {{ connection.name }}
            </p>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ connection.provider_name }} {{ t('CORTEX_MODELS.SEPARATOR') }}
              {{ connection.quota_scope }}
            </p>
          </div>
          <NextButton
            :label="platformConnectionActionLabel(connection)"
            :loading="adoptingConnectionId === connection.id"
            :disabled="connection.adopted"
            sm
            @click="adoptPlatformConnection(connection)"
          />
        </div>
      </div>
    </Dialog>
  </div>
</template>
